import React, { FunctionComponent, useEffect } from 'react'
import { View } from 'react-native'
import Animated, {
  cancelAnimation,
  Easing,
  useAnimatedStyle,
  useSharedValue,
  withRepeat,
  withTiming,
} from 'react-native-reanimated'
import Svg, { Ellipse, G, Path, Polygon, Polyline } from 'react-native-svg'

import { useTheme } from 'styled-components/native'

import { tID } from '../../utils/utils'

const VIEWBOX_WIDTH = 116
const VIEWBOX_HEIGHT = 92

// The refresh badge spins around its own centre. It is drawn in a second, square Svg laid over the
// monitor so the rotation can run on an Animated.View: SVG `<animateTransform>` is web-only.
const BADGE_CENTER_X = 92.8985507
const BADGE_CENTER_Y = 23.1681416
const BADGE_HALF_SIDE = 23 // badge radius (21.17) plus half its 2.5 stroke, rounded up
const BADGE_SIDE = BADGE_HALF_SIDE * 2

/**
 * Monitor with a list and a spinning refresh badge, shown while results are pending.
 */
export const AwaitingResultsIllustration: FunctionComponent<AwaitingResultsIllustrationProps> = ({
  width = 112,
  strokeColor,
}) => {
  const { colors } = useTheme()
  const stroke = strokeColor ?? colors.iconActive
  const height = (width * VIEWBOX_HEIGHT) / VIEWBOX_WIDTH
  const scale = width / VIEWBOX_WIDTH

  const rotation = useSharedValue(0)
  useEffect(() => {
    rotation.value = withRepeat(withTiming(-360, { duration: 8000, easing: Easing.linear }), -1)
    return () => cancelAnimation(rotation)
  }, [rotation])
  const spinStyle = useAnimatedStyle(() => ({ transform: [{ rotate: `${rotation.value}deg` }] }))

  return (
    <View style={{ width, height }} testID={tID('AwaitingResultsIllustration')}>
      <Svg width={width} height={height} viewBox={`0 0 ${VIEWBOX_WIDTH} ${VIEWBOX_HEIGHT}`}>
        <G
          fill='none'
          fillRule='evenodd'
          stroke={stroke}
          strokeWidth='2.5'
          strokeLinejoin='round'
          transform='translate(2 20.725664)'
        >
          <Path d='M90.4320626,55.0714485 C90.4320626,57.2349697 88.6666714,59.0051234 86.5002338,59.0051234 L3.93182881,59.0051234 C1.76932296,59.0051234 0,57.2349697 0,55.0714485 L0,3.9336749 C0,1.7701537 1.76932296,0 3.93182881,0 L86.5002338,0 C88.6666714,0 90.4320626,1.7701537 90.4320626,3.9336749 L90.4320626,55.0714485 L90.4320626,55.0714485 Z' />
          <Polygon points='7.86365761 51.1377736 82.5684049 51.1377736 82.5684049 7.86734979 7.86365761 7.86734979' />
          <Path d='M25.5568872,70 L64.8751753,70' strokeLinecap='round' />
          <Path d='M38,59.0051234 L38,70.8061481' />
          <Path d='M52,59.0051234 L52,70.8061481' />
          <G transform='translate(24.923796 17.585841)'>
            <Path
              d='M21.9915849,10.9911504 C21.9915849,17.0626619 17.0698682,21.9823009 10.9957924,21.9823009 C4.92171669,21.9823009 0,17.0626619 0,10.9911504 C0,4.91963894 4.92171669,0 10.9957924,0 C17.0698682,0 21.9915849,4.91963894 21.9915849,10.9911504 L21.9915849,10.9911504 Z'
              strokeLinecap='round'
            />
            <Polyline strokeLinecap='round' points='17.2128135 2.19823009 10.9957924 11.263731 17.5970262 19.6924332' />
            <Path d='M10.9957924,10 L0,10' strokeLinecap='round' />
            <Path d='M26.3899018,4 L39.5848527,4' />
            <Path d='M26.3899018,10 L39.5848527,10' />
            <Path d='M26.3899018,17 L39.5848527,17' />
          </G>
        </G>
      </Svg>
      <Animated.View
        style={[
          {
            position: 'absolute',
            left: (BADGE_CENTER_X - BADGE_HALF_SIDE) * scale,
            top: (BADGE_CENTER_Y - BADGE_HALF_SIDE) * scale,
            width: BADGE_SIDE * scale,
            height: BADGE_SIDE * scale,
          },
          spinStyle,
        ]}
      >
        <Svg
          width={BADGE_SIDE * scale}
          height={BADGE_SIDE * scale}
          viewBox={`${BADGE_CENTER_X - BADGE_HALF_SIDE} ${BADGE_CENTER_Y - BADGE_HALF_SIDE} ${BADGE_SIDE} ${BADGE_SIDE}`}
        >
          <G fill='none' fillRule='evenodd' stroke={stroke} strokeWidth='2.5'>
            <Ellipse fill='#F7F7F7' cx={BADGE_CENTER_X} cy={BADGE_CENTER_Y} rx='21.1014493' ry='21.1681416' />
            <G transform='translate(79.913043 8.513274)' strokeLinecap='round' strokeLinejoin='round'>
              <Path
                d='M10.6058359,26.0501198 C4.990577,25.4211236 0.625932537,20.695588 0.625932537,14.9564629 C0.625932537,13.152596 1.05614391,11.4479791 1.82277057,9.94062132'
                strokeDasharray='0.8100000107288352,4.859999957084653'
              />
              <Path d='M10.777354,3.72187105 L13.2785829,3.72187105 C19.4953874,3.72187105 24.5341131,8.78857817 24.5341131,14.9557185 C24.5341131,20.703528 20.1544612,25.4389886 14.5229443,26.0530973' />
              <Polyline points='15.2520113 0.267146018 10.777354 3.72187105 14.5606459 7.93269679' />
            </G>
          </G>
        </Svg>
      </Animated.View>
    </View>
  )
}

export type AwaitingResultsIllustrationProps = {
  width?: number
  strokeColor?: string
}
